/**
 * دالة طباعة تقرير سندات القبض والصرف
 */
export const printPaymentReceiptVouchersReport = (vouchers, summary, filters, branches) => {
  // تنسيق التاريخ
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // تنسيق المبلغ
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  // الحصول على اسم الفرع
  const getBranchName = () => {
    if (filters.branchId === 'all') return 'جميع الفروع';
    const branch = branches.find(b => b._id === filters.branchId);
    return branch ? branch.name : 'غير محدد';
  };

  // الحصول على نوع السند
  const getVoucherTypeText = () => {
    if (filters.voucherType === 'all') return 'جميع الأنواع';
    return filters.voucherType === 'receipt' ? 'سندات القبض' : 'سندات الصرف';
  };

  // إنشاء HTML للطباعة
  const printHTML = `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>تقرير سندات القبض والصرف</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 12px;
      line-height: 1.4;
      padding: 20px;
      direction: rtl;
    }
    
    .report-container {
      max-width: 297mm;
      margin: 0 auto;
    }
    
    .report-header {
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 2px solid #333;
    }
    
    .report-title {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 10px;
      color: #000;
    }
    
    .report-filters {
      font-size: 11px;
      color: #666;
      margin-bottom: 5px;
    }
    
    .summary-section {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
      margin: 20px 0;
      page-break-inside: avoid;
    }
    
    .summary-card {
      border: 1px solid #ddd;
      padding: 12px;
      border-radius: 4px;
      background: #f9f9f9;
      text-align: center;
    }
    
    .summary-label {
      font-size: 11px;
      color: #666;
      margin-bottom: 5px;
    }
    
    .summary-value {
      font-size: 18px;
      font-weight: bold;
      color: #000;
      margin: 5px 0;
    }
    
    .summary-amount {
      font-size: 14px;
      color: #30c3b0;
      font-weight: 600;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 15px;
    }
    
    th, td {
      border: 1px solid #333;
      padding: 8px;
      text-align: right;
      font-size: 11px;
    }
    
    th {
      background-color: #f5f5f5;
      font-weight: bold;
      color: #000;
    }
    
    .amount {
      font-weight: 600;
      text-align: left;
    }
    
    .total-row {
      background-color: #e8f5e9;
      font-weight: bold;
      font-size: 12px;
    }
    
    .total-row td {
      padding: 10px 8px;
    }
    
    .voucher-type-receipt {
      color: #2e7d32;
      font-weight: 600;
    }
    
    .voucher-type-payment {
      color: #c62828;
      font-weight: 600;
    }
    
    @media print {
      body {
        padding: 10px;
      }
      
      @page {
        size: A4 landscape;
        margin: 15mm;
      }
      
      .summary-section {
        page-break-inside: avoid;
      }
      
      table {
        page-break-inside: auto;
      }
      
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
    }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="report-header">
      <div class="report-title">تقرير سندات القبض والصرف</div>
      <div class="report-filters">
        ${filters.fromDate ? `من تاريخ: ${formatDate(filters.fromDate)}` : 'من البداية'} 
        ${filters.toDate ? ` | إلى تاريخ: ${formatDate(filters.toDate)}` : ' | حتى الآن'}
      </div>
      <div class="report-filters">
        الفرع: ${getBranchName()} | النوع: ${getVoucherTypeText()}
        ${filters.status !== 'all' ? ` | الحالة: ${filters.status === 'confirmed' ? 'مؤكد' : filters.status === 'pending' ? 'تحت الموافقة' : 'مرفوض'}` : ''}
      </div>
      <div class="report-filters">تاريخ الطباعة: ${formatDate(new Date().toISOString())}</div>
    </div>
    
    ${summary ? `
      <div class="summary-section">
        <div class="summary-card">
          <div class="summary-label">سندات القبض</div>
          <div class="summary-value">${summary.receiptCount} سند</div>
          <div class="summary-amount">${formatCurrency(summary.receiptSum)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">سندات الصرف</div>
          <div class="summary-value">${summary.paymentCount} سند</div>
          <div class="summary-amount">${formatCurrency(summary.paymentSum)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">الإجمالي</div>
          <div class="summary-value">${summary.totalCount} سند</div>
          <div class="summary-amount">${formatCurrency(summary.totalAmount)}</div>
        </div>
      </div>
    ` : ''}
    
    <table>
      <thead>
        <tr>
          <th style="width: 10%;">رقم السند</th>
          <th style="width: 8%;">النوع</th>
          <th style="width: 10%;">التاريخ</th>
          <th style="width: 12%;">الفرع</th>
          <th style="width: 18%;">الحساب المدين</th>
          <th style="width: 18%;">الحساب الدائن</th>
          <th style="width: 12%;">المبلغ</th>
          <th style="width: 12%;">الوصف</th>
        </tr>
      </thead>
      <tbody>
        ${vouchers.map(voucher => `
          <tr>
            <td>${voucher.voucherType === 'receipt' ? voucher.receiptNumber || '' : voucher.paymentNumber || ''}</td>
            <td class="${voucher.voucherType === 'receipt' ? 'voucher-type-receipt' : 'voucher-type-payment'}">
              ${voucher.voucherTypeAr}
            </td>
            <td>${formatDate(voucher.date)}</td>
            <td>${voucher.branch?.name || '-'}</td>
            <td>${voucher.debitAccount?.name || '-'}</td>
            <td>${voucher.creditAccount?.name || '-'}</td>
            <td class="amount">${formatCurrency(voucher.amount)}</td>
            <td style="font-size: 10px;">${voucher.description || '-'}</td>
          </tr>
        `).join('')}
        ${summary ? `
          <tr class="total-row">
            <td colspan="6" style="text-align: center;">المجموع الكلي</td>
            <td class="amount">${formatCurrency(summary.totalAmount)}</td>
            <td style="text-align: center;">${summary.totalCount} سند</td>
          </tr>
        ` : ''}
      </tbody>
    </table>
  </div>
  
  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 250);
    };
  </script>
</body>
</html>
  `;

  // فتح نافذة جديدة للطباعة
  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  
  if (printWindow) {
    printWindow.document.write(printHTML);
    printWindow.document.close();
  } else {
    alert('يرجى السماح بفتح النوافذ المنبثقة للطباعة');
  }
};

/**
 * دالة طباعة تقرير السندات المتخصصة
 */
export const printSpecializedVouchersReport = (vouchers, summary, filters, branches, templates) => {
  // تنسيق التاريخ
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // تنسيق المبلغ
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  // الحصول على اسم الفرع
  const getBranchName = () => {
    if (filters.branchId === 'all') return 'جميع الفروع';
    const branch = branches.find(b => b._id === filters.branchId);
    return branch ? branch.name : 'غير محدد';
  };

  // الحصول على أسماء القوالب
  const getTemplateNames = () => {
    // إذا لم يتم اختيار أي قالب أو كانت المصفوفة فارغة
    if (!filters.templateIds || filters.templateIds.length === 0) {
      return 'جميع القوالب';
    }
    
    // الحصول على أسماء القوالب المختارة
    const selectedTemplates = filters.templateIds
      .map(id => {
        const template = templates.find(t => t._id === id);
        return template ? template.name : null;
      })
      .filter(name => name !== null);
    
    // إذا لم يتم العثور على أي قالب
    if (selectedTemplates.length === 0) {
      return 'غير محدد';
    }
    
    // إرجاع الأسماء مفصولة بفواصل
    return selectedTemplates.join(', ');
  };

  // الحصول على نوع السند
  const getVoucherTypeText = (type) => {
    switch (type) {
      case 'receipt': return 'قبض';
      case 'payment': return 'صرف';
      case 'journal': return 'قيد';
      default: return type;
    }
  };

  // إنشاء HTML للطباعة
  const printHTML = `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>تقرير السندات المتخصصة</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 12px;
      line-height: 1.4;
      padding: 20px;
      direction: rtl;
    }
    
    .report-container {
      max-width: 297mm;
      margin: 0 auto;
    }
    
    .report-header {
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 2px solid #333;
    }
    
    .report-title {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 10px;
      color: #000;
    }
    
    .report-filters {
      font-size: 11px;
      color: #666;
      margin-bottom: 5px;
    }
    
    .summary-section {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 15px;
      margin: 20px 0;
      page-break-inside: avoid;
    }
    
    .summary-card {
      border: 1px solid #ddd;
      padding: 12px;
      border-radius: 4px;
      background: #f9f9f9;
      text-align: center;
    }
    
    .summary-label {
      font-size: 11px;
      color: #666;
      margin-bottom: 5px;
    }
    
    .summary-value {
      font-size: 18px;
      font-weight: bold;
      color: #000;
      margin: 5px 0;
    }
    
    .summary-amount {
      font-size: 14px;
      color: #30c3b0;
      font-weight: 600;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 15px;
    }
    
    th, td {
      border: 1px solid #333;
      padding: 8px;
      text-align: right;
      font-size: 11px;
    }
    
    th {
      background-color: #f5f5f5;
      font-weight: bold;
      color: #000;
    }
    
    .amount {
      font-weight: 600;
      text-align: left;
    }
    
    .total-row {
      background-color: #e8f5e9;
      font-weight: bold;
      font-size: 12px;
    }
    
    .total-row td {
      padding: 10px 8px;
    }
    
    .voucher-type-receipt {
      color: #2e7d32;
      font-weight: 600;
    }
    
    .voucher-type-payment {
      color: #c62828;
      font-weight: 600;
    }
    
    .voucher-type-journal {
      color: #1565c0;
      font-weight: 600;
    }
    
    @media print {
      body {
        padding: 10px;
      }
      
      @page {
        size: A4 landscape;
        margin: 15mm;
      }
      
      .summary-section {
        page-break-inside: avoid;
      }
      
      table {
        page-break-inside: auto;
      }
      
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
    }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="report-header">
      <div class="report-title">تقرير السندات المتخصصة</div>
      <div class="report-filters">
        ${filters.fromDate ? `من تاريخ: ${formatDate(filters.fromDate)}` : 'من البداية'} 
        ${filters.toDate ? ` | إلى تاريخ: ${formatDate(filters.toDate)}` : ' | حتى الآن'}
      </div>
      <div class="report-filters">
        الفرع: ${getBranchName()} | القوالب: ${getTemplateNames()}
        ${filters.status !== 'all' ? ` | الحالة: ${filters.status === 'approved' ? 'مؤكد' : 'قيد الانتظار'}` : ''}
      </div>
      <div class="report-filters">تاريخ الطباعة: ${formatDate(new Date().toISOString())}</div>
    </div>
    
    ${summary ? `
      <div class="summary-section">
        <div class="summary-card">
          <div class="summary-label">سندات القبض</div>
          <div class="summary-value">${summary.receiptCount} سند</div>
          <div class="summary-amount">${formatCurrency(summary.receiptSum)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">سندات الصرف</div>
          <div class="summary-value">${summary.paymentCount} سند</div>
          <div class="summary-amount">${formatCurrency(summary.paymentSum)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">سندات القيد</div>
          <div class="summary-value">${summary.journalCount} سند</div>
          <div class="summary-amount">${formatCurrency(summary.journalSum)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">الإجمالي</div>
          <div class="summary-value">${summary.totalCount} سند</div>
          <div class="summary-amount">${formatCurrency(summary.totalAmount)}</div>
        </div>
      </div>
    ` : ''}
    
    <table>
      <thead>
        <tr>
          <th style="width: 10%;">رقم السند</th>
          <th style="width: 12%;">القالب</th>
          <th style="width: 8%;">النوع</th>
          <th style="width: 10%;">التاريخ</th>
          <th style="width: 10%;">الفرع</th>
          <th style="width: 15%;">الحساب المدين</th>
          <th style="width: 15%;">الحساب الدائن</th>
          <th style="width: 10%;">المبلغ</th>
          <th style="width: 10%;">رقم العملية</th>
        </tr>
      </thead>
      <tbody>
        ${vouchers.map(voucher => `
          <tr>
            <td>${voucher.voucherNumber}</td>
            <td style="font-size: 10px;">${voucher.templateId?.name || '-'}</td>
            <td class="voucher-type-${voucher.voucherType}">
              ${getVoucherTypeText(voucher.voucherType)}
            </td>
            <td>${formatDate(voucher.voucherDate)}</td>
            <td>${voucher.branch?.name || '-'}</td>
            <td style="font-size: 10px;">${voucher.debitAccountId?.name || '-'}</td>
            <td style="font-size: 10px;">${voucher.creditAccountId?.name || '-'}</td>
            <td class="amount">${formatCurrency(voucher.amount)}</td>
            <td>${voucher.operationNumber || '-'}</td>
          </tr>
        `).join('')}
        ${summary ? `
          <tr class="total-row">
            <td colspan="7" style="text-align: center;">المجموع الكلي</td>
            <td class="amount">${formatCurrency(summary.totalAmount)}</td>
            <td style="text-align: center;">${summary.totalCount} سند</td>
          </tr>
        ` : ''}
      </tbody>
    </table>
  </div>
  
  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 250);
    };
  </script>
</body>
</html>
  `;

  // فتح نافذة جديدة للطباعة
  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  
  if (printWindow) {
    printWindow.document.write(printHTML);
    printWindow.document.close();
  } else {
    alert('يرجى السماح بفتح النوافذ المنبثقة للطباعة');
  }
};
